import Phaser from 'phaser';
import { CONFIG } from '../config.js';
import { SaveSystem } from '../systems/SaveSystem.js';
import { getAudio } from '../systems/AudioSystem.js';

const LEVEL_META = {
  basic:    { label: 'BASIC',    zone: 'Whispering Forest', tag: '34 verbs · Past + Participle', color: 0xd946ef },
  advanced: { label: 'ADVANCED', zone: 'Crystal Caves',     tag: '54 verbs · Past Simple only',  color: 0x06b6d4 },
  expert:   { label: 'EXPERT',   zone: "Wizard's Sanctum",  tag: '54 verbs · Past + Participle', color: 0xfde047 },
};

export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
  }

  create() {
    const { width, height } = this.scale;
    const audio = getAudio();

    this.add.tileSprite(0, 0, width, height, 'tile-grass').setOrigin(0, 0).setAlpha(0.65);
    this.add.rectangle(0, 0, width, height, 0x000000, 0.45).setOrigin(0, 0);

    const titleTop = this.add
      .text(width / 2, height * 0.10, 'VERB', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '52px',
        color: '#d946ef',
        stroke: '#0f0d2c',
        strokeThickness: 8,
      })
      .setOrigin(0.5);
    const titleBot = this.add
      .text(width / 2, height * 0.175, 'STRIKE', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '52px',
        color: '#06b6d4',
        stroke: '#0f0d2c',
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: [titleTop, titleBot],
      y: '-=6',
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add
      .text(width / 2, height * 0.245, 'Hecho para Emma', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#fbbf24',
      })
      .setOrigin(0.5);

    const save = SaveSystem.load();
    audio.setEnabled(save.audioEnabled !== false);

    const advancedUnlocked = SaveSystem.isAdvancedUnlocked();
    const expertUnlocked = SaveSystem.isExpertUnlocked();

    this.makeLevelButton(width / 2, height * 0.36, 'basic',    true,             save);
    this.makeLevelButton(width / 2, height * 0.50, 'advanced', advancedUnlocked, save);
    this.makeLevelButton(width / 2, height * 0.64, 'expert',   expertUnlocked,   save);

    // Tiny stats line
    const stats = [
      `Best score ${save.bestScore || 0}`,
      `Kills ${save.totalKills || 0}`,
      `Combo x${save.bestCombo || 0}`,
    ].join('  ·  ');
    this.add
      .text(width / 2, height * 0.745, stats, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#06b6d4',
      })
      .setOrigin(0.5);

    if (save.basicCompleted || save.advancedCompleted || save.expertCompleted || save.totalKills) {
      this.makeButton(
        width / 2,
        height * 0.81,
        220,
        34,
        'RESET PROGRESS',
        0x312e81,
        () => {
          if (window.confirm('Borrar todo el progreso guardado?')) {
            SaveSystem.reset();
            this.scene.restart();
          }
        },
        '9px'
      );
    }

    // Audio toggle pill
    const audioBtn = this.makeButton(
      width - 38,
      32,
      52,
      36,
      audio.enabled ? 'ON' : 'OFF',
      audio.enabled ? 0x22c55e : 0x6b7280,
      () => {
        const enabled = audio.toggle();
        SaveSystem.setAudioEnabled(enabled);
        audioBtn.label.setText(enabled ? 'ON' : 'OFF');
        audioBtn.bg.setFillStyle(enabled ? 0x22c55e : 0x6b7280);
      },
      '10px'
    );
    this.add
      .text(width - 38, 60, 'AUDIO', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '7px',
        color: '#9ca3af',
      })
      .setOrigin(0.5, 0);

    this.add
      .text(width / 2, height - 60, 'WASD/ARROWS  ·  1·2·3 ANSWER', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#9ca3af',
      })
      .setOrigin(0.5);

    // Pixie Studios logo at bottom (~200px wide).
    if (this.textures.exists('pixie-logo')) {
      const tex = this.textures.get('pixie-logo').getSourceImage();
      const targetW = 200;
      const scale = targetW / tex.width;
      this.add
        .image(width / 2, height - 26, 'pixie-logo')
        .setOrigin(0.5)
        .setScale(scale)
        .setAlpha(0.95);
    } else {
      this.add
        .text(width / 2, height - 26, 'PIXIE STUDIOS', {
          fontFamily: '"Press Start 2P", monospace',
          fontSize: '9px',
          color: '#d946ef',
        })
        .setOrigin(0.5);
    }

    // Unlock audio on first input gesture (required by mobile browsers).
    this.input.once('pointerdown', () => audio.unlock());
    this.input.keyboard.once('keydown', () => audio.unlock());
  }

  makeLevelButton(x, y, level, unlocked, save) {
    const meta = LEVEL_META[level];
    const W = 320;
    const H = 78;
    const completed = save[`${level}Completed`];

    const container = this.add.container(x, y);
    const fill = unlocked ? meta.color : 0x1f1d4d;
    const bg = this.add
      .rectangle(0, 0, W, H, fill, unlocked ? 1 : 0.55)
      .setStrokeStyle(3, unlocked ? 0xffffff : 0x4338ca, 0.95);
    container.add(bg);

    const lockOrCheck = completed ? '★ ' : unlocked ? '' : '🔒 ';
    const labelText = this.add
      .text(0, -14, `${lockOrCheck}${meta.label}`, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '20px',
        color: '#ffffff',
        stroke: '#0f0d2c',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    container.add(labelText);

    const zoneText = this.add
      .text(0, 10, meta.zone, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: unlocked ? '#fde047' : '#9ca3af',
      })
      .setOrigin(0.5);
    container.add(zoneText);

    const tagText = this.add
      .text(0, 26, meta.tag, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '7px',
        color: unlocked ? '#ffffff' : '#9ca3af',
      })
      .setOrigin(0.5);
    container.add(tagText);

    if (!unlocked) {
      container.setAlpha(0.6);
    }

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setScale(1.03));
    bg.on('pointerout', () => bg.setScale(1));
    bg.on('pointerdown', () => bg.setScale(0.97));
    bg.on('pointerup', () => {
      bg.setScale(1);
      if (!unlocked) {
        this.flashLocked(container, level);
        return;
      }
      const audio = getAudio();
      audio.unlock();
      audio.correct();
      this.scene.start('GameScene', { level });
    });

    return container;
  }

  flashLocked(container, level) {
    const blockerY = container.y - 50;
    const msg = level === 'advanced'
      ? 'Termina BASIC, Emma'
      : 'Termina ADVANCED, Emma';
    const t = this.add
      .text(this.scale.width / 2, blockerY, msg, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '9px',
        color: '#ef4444',
        stroke: '#0f0d2c',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: t,
      alpha: 0,
      y: blockerY - 14,
      duration: 1400,
      ease: 'Quad.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  makeButton(x, y, w, h, label, fillColor, onClick, fontSize = '18px') {
    const container = this.add.container(x, y);
    const bg = this.add.rectangle(0, 0, w, h, fillColor, 1).setStrokeStyle(3, 0xffffff, 0.9);
    const text = this.add
      .text(0, 0, label, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize,
        color: '#ffffff',
      })
      .setOrigin(0.5);
    container.add([bg, text]);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setScale(1.05));
    bg.on('pointerout', () => bg.setScale(1));
    bg.on('pointerdown', () => bg.setScale(0.95));
    bg.on('pointerup', () => {
      bg.setScale(1);
      onClick();
    });
    container.bg = bg;
    container.label = text;
    return container;
  }
}
